import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useExecutionAnalytics } from '../../hooks/useExecutionAnalytics';
import BaseChart from '../charts/BaseChart';

/**
 * Analytics Page Component
 *
 * Displays execution analytics using ApexCharts:
 * - Execution trends
 * - Error analysis
 * - Performance metrics
 * - Cost analysis
 * - Template comparison
 * - Provider/model performance
 */
const Analytics = () => {
  const [period, setPeriod] = useState('30d');
  const { isAuthenticated, loading: authLoading } = useAuth();

  const { data, loading, error } = useExecutionAnalytics(period);

  // Show loading if auth is still initializing
  if (authLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to view analytics data.</Alert>
      </Container>
    );
  }

  const handlePeriodChange = event => {
    setPeriod(event.target.value);
  };

  // Prepare execution trends chart data
  const prepareTrendsChartData = () => {
    if (!data.trends?.data || data.trends.data.length === 0) {
      return { series: [], categories: [] };
    }

    const categories = data.trends.data.map(point => point.date);
    const totalSeries = {
      name: 'Total Executions',
      data: data.trends.data.map(point => point.total_executions),
    };
    const successfulSeries = {
      name: 'Successful',
      data: data.trends.data.map(point => point.successful_executions),
    };
    const failedSeries = {
      name: 'Failed',
      data: data.trends.data.map(point => point.failed_executions),
    };

    return {
      series: [totalSeries, successfulSeries, failedSeries],
      categories,
    };
  };

  // Prepare error analysis chart data
  const prepareErrorChartData = () => {
    if (!data.errors?.errors || data.errors.errors.length === 0) {
      return { series: [], categories: [] };
    }

    const topErrors = data.errors.errors.slice(0, 10);
    const categories = topErrors.map(error => error.error_code);
    const series = [
      {
        name: 'Occurrences',
        data: topErrors.map(error => error.occurrence_count),
      },
    ];

    return { series, categories };
  };

  // Prepare cost analysis chart data
  const prepareCostChartData = () => {
    if (!data.costs?.cost_by_provider) {
      return { series: [], categories: [] };
    }

    const providers = Object.keys(data.costs.cost_by_provider);
    const costs = Object.values(data.costs.cost_by_provider);
    const series = [
      {
        name: 'Cost',
        data: costs,
      },
    ];

    return { series, categories: providers };
  };

  // Prepare template comparison chart data
  const prepareTemplateComparisonData = () => {
    if (
      !data.templateComparison?.templates ||
      data.templateComparison.templates.length === 0
    ) {
      return { series: [], categories: [] };
    }

    const topTemplates = data.templateComparison.templates.slice(0, 10);
    const categories = topTemplates.map(t => t.template_name || t.template_id);
    const series = [
      {
        name: 'Total Executions',
        data: topTemplates.map(t => t.total_executions),
      },
    ];

    return { series, categories };
  };

  const trendsData = prepareTrendsChartData();
  const errorData = prepareErrorChartData();
  const costData = prepareCostChartData();
  const templateData = prepareTemplateComparisonData();

  const hasError = Object.values(error).some(e => e !== null);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Execution Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive analytics for template executions, performance, and
          costs
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={period}
              label="Time Period"
              onChange={handlePeriodChange}
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {hasError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading analytics data. Please try again.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Execution Trends */}
        <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
          <BaseChart
            title="Execution Trends"
            subtitle={`Daily execution counts over ${period}`}
            type="line"
            series={trendsData.series}
            options={{
              xaxis: {
                categories: trendsData.categories,
              },
              stroke: {
                curve: 'smooth',
                width: 3,
              },
              colors: ['#1976d2', '#4caf50', '#f44336'],
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                verticalAlign: 'bottom',
                offsetY: 0,
              },
              chart: {
                offsetX: 0,
                offsetY: 0,
                width: '100%',
                toolbar: {
                  offsetX: 0,
                  offsetY: 0,
                },
              },
              grid: {
                padding: {
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5,
                },
              },
            }}
            height={400}
            loading={loading.trends}
          />
        </Grid>

        {/* Error Analysis */}
        <Grid size={{ xs: 12, md: 6 }}>
          <BaseChart
            title="Error Analysis"
            subtitle="Top 10 error codes by occurrence"
            type="bar"
            series={errorData.series}
            options={{
              xaxis: {
                categories: errorData.categories,
              },
              colors: ['#f44336'],
              legend: {
                show: false,
              },
              chart: {
                width: '100%',
                toolbar: {
                  offsetX: 0,
                  offsetY: 0,
                },
              },
              grid: {
                padding: {
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5,
                },
              },
            }}
            height={400}
            loading={loading.errors}
          />
        </Grid>

        {/* Cost Analysis */}
        <Grid size={{ xs: 12, md: 6 }}>
          <BaseChart
            title="Cost Analysis"
            subtitle="Cost distribution by provider"
            type="bar"
            series={costData.series}
            options={{
              xaxis: {
                categories: costData.categories,
              },
              colors: ['#ff9800'],
              yaxis: {
                labels: {
                  formatter: function (val) {
                    return '$' + val.toFixed(2);
                  },
                },
              },
              legend: {
                show: false,
              },
              chart: {
                width: '100%',
                toolbar: {
                  offsetX: 0,
                  offsetY: 0,
                },
              },
              grid: {
                padding: {
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5,
                },
              },
            }}
            height={400}
            loading={loading.costs}
          />
        </Grid>

        {/* Template Comparison */}
        <Grid size={{ xs: 12 }}>
          <BaseChart
            title="Template Comparison"
            subtitle="Top 10 templates by execution count"
            type="bar"
            series={templateData.series}
            options={{
              xaxis: {
                categories: templateData.categories,
              },
              colors: ['#9c27b0'],
              plotOptions: {
                bar: {
                  horizontal: true,
                },
              },
              legend: {
                show: false,
              },
              chart: {
                width: '100%',
                toolbar: {
                  offsetX: 0,
                  offsetY: 0,
                },
              },
              grid: {
                padding: {
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5,
                },
              },
            }}
            height={400}
            loading={loading.templateComparison}
          />
        </Grid>

        {/* Performance Metrics Summary */}
        {data.performance && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics Summary
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Avg Execution Time
                    </Typography>
                    <Typography variant="h6">
                      {data.performance.metrics?.avg_execution_time
                        ? `${data.performance.metrics.avg_execution_time.toFixed(2)}s`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      P95 Execution Time
                    </Typography>
                    <Typography variant="h6">
                      {data.performance.metrics?.p95_execution_time
                        ? `${data.performance.metrics.p95_execution_time.toFixed(2)}s`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                    <Typography variant="h6">
                      {data.performance.metrics?.success_rate
                        ? `${(data.performance.metrics.success_rate * 100).toFixed(1)}%`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Executions
                    </Typography>
                    <Typography variant="h6">
                      {data.performance.metrics?.total_executions?.toLocaleString() ||
                        'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Analytics;
